import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getIsBaseRouteVisited } from '../redux/App/App.slice';
import { getClosestBaseRoute, isBaseRoute } from '../utils/routes';
import { LocationState } from '../types/locationState.interface';

type UseBackButtonClick = undefined | (() => void);

// The button is inactive if user is now on one of the base routes
// Button logic:
// If any base route was visited - back button works as browser's navigate.back
// If no base routes visited - we check if there's any saved location.state and redirect there
// If no saved state presented - back button is leading to the closest base route
export const useBackButtonClick = (): UseBackButtonClick => {
  const location = useLocation();
  const navigate = useNavigate();
  const isBaseRouteVisited = useSelector(getIsBaseRouteVisited);

  const isBackButtonActive = !isBaseRoute(location.pathname);

  const onBackButtonClick = useCallback(() => {
    if (!isBackButtonActive) {
      return;
    }

    if (isBaseRouteVisited) {
      navigate(-1);

      return;
    }

    const locationState = location.state as LocationState | null;
    if (locationState && locationState.backUrl) {
      navigate({ pathname: locationState.backUrl, search: locationState.backUrlQuery });

      return;
    }

    navigate(getClosestBaseRoute(location.pathname));

    return;
  }, [isBackButtonActive, isBaseRouteVisited, navigate, location.pathname]);

  return isBackButtonActive ? onBackButtonClick : undefined;
};
